import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckCircle2, Hourglass, XCircle, Ban, Archive, Zap } from 'lucide-react';
import client from '../../api/client';
import { formatDateSafe } from '../../utils/formatDateSafe';
import { NAVY, AMBER, bubbleStyle } from '../../styles/tokens';

/**
 * COLA DE APROBACIÓN — reescrita en V62 para la cadena de DOS niveles.
 *
 * La versión anterior tenía la cadena fija de 5 niveles cableada (L1 Supervisor … L5
 * Directora) y mostraba `days_requested` y `current_level`, columnas que V59 retiró.
 *
 * ⚠ El badge de nivel dejó de ser un NÚMERO y pasó a ser el ROL: con dos niveles, "L1"
 * no le dice nada a nadie y "Ingeniero(a)" sí.
 *
 * ⚠⚠ ESTA PANTALLA MUESTRA EL TIPO DE AUSENCIA, y el calendario NO — no es una
 * inconsistencia: acá está el aprobador de ESA solicitud, que necesita saber qué autoriza.
 * El calendario lo ve toda la empresa, y ahí el tipo sería dato de salud circulando.
 */

// ⚠ ANTI-DIVERGENCIA: espeja `PTO_LEVELS` de backend/src/shared/services/
// ptoApprovalChainService.js, que es la fuente de verdad de la cadena. Si allá se agrega
// un nivel, acá hay que agregarlo — de lo contrario el badge cae al genérico `Nivel N`.
const LEVEL_ROLE = { 1: 'Ingeniero(a)', 2: 'Socio(a)' };
const TYPE_LABELS = { vacation: 'Vacaciones', sick: 'Enfermedad' };

/**
 * 🔴 CUANDO LA SOLICITUD YA CERRÓ, TODO SALE DE ACÁ: texto, color, fondo, borde e ícono.
 *
 * Está keyeado por `request_status` —el estado de la SOLICITUD— y no por `par.status` —el
 * de MI FILA—, que es la regla que ya rige para el texto. Antes el texto la respetaba pero
 * el color no: una DENEGADA salía en VERDE con un ✓ porque la rama de "cerrada" tenía el
 * verde hardcodeado, y `rejected` en mi fila se leía como "acción completada".
 *
 * ⚠⚠ Que texto y color sean CAMPOS DEL MISMO OBJETO es el punto: separados, se puede
 * arreglar uno y olvidar el otro — que es exactamente lo que pasó. No los separes.
 */
const REQ_CLOSED = {
  approved:  { texto: 'Aprobada',  color: '#16a34a', bg: '#dcfce7', borde: '#16a34a', Icono: CheckCircle2 },
  denied:    { texto: 'Denegada',  color: '#dc2626', bg: '#fee2e2', borde: '#dc2626', Icono: XCircle },
  // ⚠ 'cancelled' HOY NO PUEDE LLEGAR: `getActiveForApprover` filtra
  // `r.status IN ('pending','approved','denied')`, así que una cancelada no entra en la
  // cola. Está mapeada igual, en gris y con su propio ícono, para que el día que ese
  // filtro se ensanche NO herede el verde por defecto — que es justo el bug que esto
  // arregla. Ni verde ni rojo: cancelar no es aprobar ni denegar.
  cancelled: { texto: 'Cancelada', color: '#64748b', bg: '#f1f5f9', borde: '#94a3b8', Icono: Ban },
};

const fmtHoras = (h) => `${(Number(h) || 0).toFixed(2)} h`;

/**
 * QUÉ ES ESTA COLA (decidido en el pase visual de V62): **pendientes + historial de lo que
 * pasó por mis manos**, con el filtro por defecto en PENDIENTES.
 *
 * No es solo "lo que me toca actuar": que la solicitud NO desaparezca al aprobarla es
 * comportamiento de V13 que V60 conservó a propósito (el aprobador quiere ver que siguió
 * avanzando). Pero abrir la pantalla en "todo" mezclaba lo accionable con lo cerrado, así
 * que el default es Pendientes y el historial queda a un clic.
 */
export default function PTOReviewListPage() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  // ⚠⚠ V65 — EL FILTRO INICIAL SE DECIDE CON LOS DATOS, NO ANTES DE TENERLOS.
  //
  // Arrancaba fijo en 'pendientes' (= "Tu turno"). Para quien está en la cadena eso es
  // correcto y es la decisión de V62. Pero **el admin NO está en la cadena**: desde que
  // su cola es global (V65) ve todo y no le toca actuar nada, así que "Tu turno" le queda
  // ESTRUCTURALMENTE VACÍO SIEMPRE — abría la pantalla en blanco sobre una cola con 5
  // solicitudes. Es la misma trampa que el default 'pendientes' ya tendió en el pase de
  // V62, ahora por otra puerta.
  //
  // La condición NO es "si es admin": es "si no hay nada en mi turno". Así vale para
  // cualquier rol que quede fuera de la cadena en el futuro, sin enumerarlos.
  const [filtro, setFiltro] = useState(null);   // null = todavía no se decidió

  useEffect(() => {
    // `view=active` trae también lo que uno ya aprobó y sigue avanzando: la solicitud no
    // desaparece de la vista al actuar (comportamiento de V13, conservado en V60).
    client.get('/api/vacation/my-approvals', { params: { view: 'active' } })
      .then(r => {
        const filas = r.data || [];
        setRecords(filas);
        // Se fija UNA vez, al llegar los datos, y después manda el usuario.
        setFiltro(filas.some(x => x.status === 'pending' && x.request_status === 'pending')
          ? 'pendientes' : 'todas');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // ⚠ "Cerrada" es propiedad de la SOLICITUD, no de mi fila de la cadena: mi nivel puede
  // estar 'approved' mientras la solicitud sigue 'pending' esperando al otro. Confundirlas
  // fue el bug del pase visual (una solicitud aprobada decía "espera Ingeniero(a)").
  const cerrada   = (r) => r.request_status !== 'pending';
  const miTurno   = (r) => r.status === 'pending' && !cerrada(r);
  const enCurso   = (r) => !cerrada(r);

  const conteos = {
    pendientes: records.filter(miTurno).length,
    enCurso:    records.filter(r => enCurso(r) && !miTurno(r)).length,
    cerradas:   records.filter(cerrada).length,
  };
  const visibles = records.filter(r =>
    filtro === 'pendientes' ? miTurno(r)
    : filtro === 'enCurso'  ? (enCurso(r) && !miTurno(r))
    : filtro === 'cerradas' ? cerrada(r)
    : true);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando…</div>;

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 820, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: 0 }}>Aprobación de Ausencias</h1>
        {conteos.pendientes > 0 && (
          <span style={{ background: '#eab308', color: '#fff', borderRadius: 10, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
            {conteos.pendientes}
          </span>
        )}
      </div>

      {/* Resumen — mismo patrón que "Mis Solicitudes": las tarjetas son el filtro. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
        {[
          { key: 'pendientes', label: 'Tu turno',  count: conteos.pendientes, color: '#eab308', Icono: Clock },
          { key: 'enCurso',    label: 'En curso',  count: conteos.enCurso,    color: '#64748b', Icono: Hourglass },
          // ⚠ Gris y con ícono neutro A PROPÓSITO: esta tarjeta cuenta aprobadas Y
          // denegadas juntas, así que pintarla de verde con un ✓ diría "aprobadas" —
          // la misma mentira que el color de la fila, en chico.
          { key: 'cerradas',   label: 'Cerradas',  count: conteos.cerradas,   color: '#475569', Icono: Archive },
        ].map(s => (
          <div
            key={s.key}
            onClick={() => setFiltro(filtro === s.key ? 'todas' : s.key)}
            style={{
              ...bubbleStyle, padding: '10px 12px', cursor: 'pointer', textAlign: 'center',
              border: filtro === s.key ? `2px solid ${s.color}` : undefined,
            }}
          >
            <s.Icono size={18} color={s.color} style={{ marginBottom: 4 }} />
            <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.count}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {visibles.length === 0 ? (
        /* ⚠⚠ EL VACÍO TIENE QUE DECIR QUE HAY ALGO EN OTRO LADO.
           El filtro por defecto es "Pendientes", así que a quien no tiene nada por actuar
           —el caso normal de un socio al día— le salía "No hay ausencias esperando tu
           revisión" y punto: cierto, pero un callejón sin salida con sus solicitudes
           cerradas escondidas detrás de una tarjeta. Se reportó como "denegué una y no la
           veo". El backend las devolvía bien; era esta pantalla. */
        <div style={{ ...bubbleStyle, padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
          <div>
            {filtro === 'pendientes'
              ? 'No hay ausencias esperando tu revisión.'
              : 'Nada para mostrar con este filtro.'}
          </div>
          {records.length > 0 && filtro !== 'todas' && (
            <button
              type="button"
              onClick={() => setFiltro('todas')}
              style={{
                marginTop: 10, background: 'none', border: 'none', padding: 0,
                color: AMBER, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Ver las {records.length} que pasaron por tus manos
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visibles.map(r => {
            const estaCerrada = cerrada(r);
            const esMiTurno   = miTurno(r);
            const yaAprobe    = r.status === 'approved';
            // 🔴 V65 — `skipped` = NADIE ACTUÓ ese nivel. Solo llega acá para el admin.
            // Mostrar "Socio(a)" sería mentir: nadie firmó como socio, la solicitud se
            // aprobó sola por quien la cargó. El badge dice qué pasó, no qué nivel es.
            const autoAprobada = r.status === 'skipped';
            const miRol       = autoAprobada
              ? 'Auto-aprobada'
              : (LEVEL_ROLE[r.level] || `Nivel ${r.level}`);
            // Con DOS niveles, "el otro" se deriva sin pedirle nada al backend: si mi fila
            // es la 1, el que sigue es la 2, y viceversa. `current_level` no existe más
            // (V59 la retiró para no tener el mismo hecho en dos lugares).
            const otroRol = LEVEL_ROLE[r.level === 1 ? 2 : 1] || 'el otro nivel';

            // 🔴 LA SOLICITUD CERRADA MANDA SOBRE MI FILA. Antes el texto se derivaba solo
            // de `r.status` (mi nivel), así que una solicitud YA APROBADA mostraba
            // "Aprobaste · espera Socio(a)" — y si el otro nivel estaba `skipped`
            // (smart-submit o carga del socio), no esperaba a nadie en absoluto.
            // Sabiendo que la solicitud sigue 'pending', SÍ es correcto decir que espera al
            // otro nivel: con dos niveles, si el mío ya no está pendiente, el pendiente es
            // el otro.
            // Cerrada → todo (texto, color, ícono) sale de REQ_CLOSED, por `request_status`.
            const cierre = REQ_CLOSED[r.request_status];
            const estado = autoAprobada
              ? { texto: 'Auto-aprobada — nadie la firmó', color: '#7c3aed', bg: '#f3e8ff',
                  borde: '#a78bfa', Icono: Zap, iconoColor: '#7c3aed' }
              : estaCerrada && cierre
              ? { ...cierre, iconoColor: cierre.color }
              : esMiTurno
                ? { texto: 'Tu turno', color: '#a16207', bg: '#fef9c3', borde: '#eab308', Icono: Clock, iconoColor: '#eab308' }
                : yaAprobe
                  ? { texto: `Aprobaste · espera ${otroRol}`, color: '#16a34a', bg: '#dcfce7', borde: '#16a34a', Icono: CheckCircle2, iconoColor: '#16a34a' }
                  : { texto: `Espera ${otroRol}`, color: '#64748b', bg: '#f1f5f9', borde: '#94a3b8', Icono: Hourglass, iconoColor: '#94a3b8' };

            const { Icono } = estado;

            return (
              <div
                key={`${r.request_id}-${r.level}`}
                onClick={() => navigate(`/vacaciones/${r.request_id}`)}
                style={{
                  ...bubbleStyle, padding: '14px 18px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 14,
                  borderLeft: `3px solid ${estado.borde}`,
                  opacity: esMiTurno ? 1 : 0.9,
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', background: estado.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Icono size={18} color={estado.iconoColor} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: NAVY }}>{r.employee_name}</span>
                    <span style={{ fontSize: 12, color: '#64748b' }}>· {TYPE_LABELS[r.type] || r.type}</span>
                    {r.project_code && (
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>· {r.project_code}</span>
                    )}
                    {/* El badge sale del MISMO objeto que el resto: si el color de la fila
                        dice denegada, este no puede decir aprobada. */}
                    {estaCerrada && cierre && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 8px', borderRadius: 10,
                        background: cierre.bg, color: cierre.color,
                      }}>
                        {cierre.texto}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    {formatDateSafe(r.start_date, { day: '2-digit', month: 'short' })}
                    {' — '}
                    {formatDateSafe(r.end_date, { day: '2-digit', month: 'short' })}
                    {' · '}{fmtHoras(r.hours_requested)}
                    <span style={{ marginLeft: 8, color: estado.color, fontWeight: 600 }}>· {estado.texto}</span>
                  </div>
                </div>

                <div style={{
                  padding: '3px 10px', borderRadius: 10, background: estado.bg, color: estado.color,
                  fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                }}>
                  {miRol}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
